/**
 * Main class for managing the Mega Menu widget.
 */
class MegaMenu {
	widget;
	menuToggleEl;
	menuBodyEl;
	menuBlockerEl;
	queries;

	/**
	 * Instantiate class for each widget.
	 * 
	 * @param {HTMLElement} widget
	 */
	constructor( widget ) {
		this.widget = widget;
		this.menuToggleEl = widget.querySelector( '.menu-toggle' );
		this.menuBodyEl = widget.querySelector( '.menu-body' );
		this.menuBlockerEl = widget.querySelector( '.menu-blocker' );
		this.submenuEls = this.menuBodyEl.querySelectorAll( ':scope > ul > li:has(.submenu)' );
		this.dropdownMenuMediaQuery = window.matchMedia( '(max-width: 1024px)' );

		if( !this.menuToggleEl || !this.menuBodyEl || !this.menuBlockerEl ) {
			return;
		}

		/**
		 * Enable submenus
		 */
		this.submenuEls.forEach( parentItem => {
			parentItem.submenu = new Submenu( this, parentItem );
		} );

		/**
		 * Set viewport events
		 */
		this.setViewport(); // Set viewport on load
		this.dropdownMenuMediaQuery.addEventListener( 'change', () => this.setViewport() ); // On media query change

		/**
		 * Main menu toggle
		 */
		this.menuToggleEl.addEventListener( 'click', () => this.toggleMenu() );

		/**
		 * Click on blocker to close submenu
		 */
		this.menuBlockerEl.addEventListener( 'click', () => this.closeSubmenus() );
	}

	/**
	 * Set menu element properties for each viewport.
	 */
	setViewport() {
		if( this.dropdownMenuMediaQuery.matches ) { // Mobile
			this.menuBodyEl.hidden = true;
			this.menuBodyEl.setAttribute( 'aria-expanded', 'false' );
			this.menuToggleEl.classList.remove( 'active' );
			this.menuToggleEl.disabled = false;
			this.submenuEls.forEach( submenuEl => submenuEl.submenu.enable() );
		} else { // Desktop
			this.menuBodyEl.hidden = false;
			this.menuBodyEl.removeAttribute( 'aria-expanded' );
			this.menuToggleEl.classList.remove( 'active' );
			this.menuToggleEl.disabled = true;
			document.body.classList.remove( 'whippet-mega-menu--active' );
		}
	}

	/**
	 * Toggle main menu open or close state.
	 */
	toggleMenu() {
		this.menuBodyEl.hidden = !this.menuBodyEl.hidden;
		this.menuBodyEl.setAttribute( 'aria-expanded', this.menuBodyEl.hidden ? 'false' : 'true' );
		this.menuToggleEl.classList.toggle( 'active' );
		document.body.classList.toggle( 'whippet-mega-menu--active' );
	}

	/**
	 * Close all submenus.
	 */
	closeSubmenus() {
		this.submenuEls.forEach( submenuEl => submenuEl.submenu.close() );
	}
}

/**
 * Class for managing a single submenu inside the Mega Menu widget.
 */
class Submenu {
	mainMenuObject;
	parentItemEl;
	toggleEl;
	bodyEl;

	/**
	 * Instantiate class for each widget.
	 * 
	 * @param {MegaMenu} mainMenuObject Main Mega Menu class.
	 * @param {HTMLElement} parentItem Submenu parent <li>.
	 */
	constructor( mainMenuObject, parentItem ) {
		/**
		 * Set properties.
		 */
		this.mainMenuObject = mainMenuObject;
		this.parentItemEl = parentItem;
		this.toggleEl = parentItem.querySelector( '.submenu-toggle' );
		this.bodyEl = parentItem.querySelector( '.submenu' );

		/**
		 * Register events
		 */
		this.toggleEl.addEventListener( 'click', () => this.toggle() ); // Toggle submenu on click
	}

	/**
	 * Toggle menu state.
	 */
	toggle() {
		this.bodyEl.hidden ? this.open() : this.close();
	}

	/**
	 * Open menu.
	 */
	open() {
		this.mainMenuObject.closeSubmenus(); // Close all submenus
		this.bodyEl.hidden = false;
		this.bodyEl.setAttribute( 'aria-expanded', 'true' );
		this.toggleEl.classList.add( 'active' );
		this.mainMenuObject.menuBlockerEl.hidden = false;
		this.toggleEl.scrollIntoView();
	}

	/**
	 * Close menu.
	 */
	close() {
		this.bodyEl.hidden = true;
		this.bodyEl.setAttribute( 'aria-expanded', 'false' );
		this.toggleEl.classList.remove( 'active' );
		this.mainMenuObject.menuBlockerEl.hidden = true;
	}

	/**
	 * Enable menu actions.
	 */
	enable() {
		this.toggleEl.classList.remove( 'active' );
		this.toggleEl.disabled = false;
		this.bodyEl.hidden = true;
	}

	/**
	 * Disable menu actions.
	 */
	disable() {
		this.toggleEl.classList.remove( 'active' );
		this.toggleEl.disabled = true;
		this.bodyEl.hidden = true;
	}
}

document.querySelectorAll( '.whippet-mega-menu' ).forEach( widget => {
	new MegaMenu( widget );
} );
